import { useEffect, useRef, useState } from 'react';

/**
 * Shown exactly once, right after `POST /members` succeeds (spec "Alta y
 * contraseña temporal": "una única vez tras el alta para copiarla y
 * entregarla al alumno"). The password lives only in the parent's state for
 * as long as this is mounted; `onClose` is the parent dropping that state,
 * which unmounts this component and removes the value from the DOM entirely.
 * Never touches `console` and never writes to any storage — rendered as
 * plain text (not an input's value) so it's also selectable/copyable by
 * hand, not only through the button.
 */
export function TemporaryPasswordNotice({ username, password, passwordIsDocument = false, onClose }) {
  const [copyState, setCopyState] = useState('idle'); // idle | copied | error
  const textRef = useRef(null);
  const noticeRef = useRef(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    const getFocusable = () => noticeRef.current?.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ) || [];
    const focusable = getFocusable();
    focusable[0]?.focus();

    function containTab(event) {
      if (event.key !== 'Tab') return;
      const items = getFocusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || !noticeRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !noticeRef.current?.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', containTab);
    return () => {
      document.removeEventListener('keydown', containTab);
      if (previousFocus?.isConnected && typeof previousFocus.focus === 'function') previousFocus.focus();
    };
  }, []);

  if (passwordIsDocument) {
    return (
      <div className="gym-admin-password-notice-backdrop">
        <div ref={noticeRef} className="gym-admin-password-notice" role="status">
          <p className="gym-admin-password-notice__lead">La contraseña es su DNI</p>
          <div className="gym-admin-password-notice__actions">
            <button type="button" className="gym-button gym-button--primary" onClick={onClose}>Cerrar aviso</button>
          </div>
        </div>
      </div>
    );
  }

  function selectPasswordText() {
    const node = textRef.current;
    if (!node || typeof window === 'undefined' || !window.getSelection) return;
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  async function handleCopy() {
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (clipboard && typeof clipboard.writeText === 'function') {
      try {
        await clipboard.writeText(password);
        setCopyState('copied');
        return;
      } catch {
        // Fall through to the manual-selection fallback below.
      }
    }
    // No async Clipboard API (older browser, insecure context, permission
    // denied): select the text itself so the admin can still copy it with
    // Ctrl/Cmd+C instead of being left with no way to grab it at all.
    selectPasswordText();
    setCopyState('error');
  }

  return (
    <div className="gym-admin-password-notice-backdrop">
      <div ref={noticeRef} className="gym-admin-password-notice" role="status">
        <p className="gym-admin-password-notice__lead">
          Contraseña temporal para <strong>{username}</strong>. Copiala y entregásela: no se va a
          volver a mostrar.
        </p>
        <div className="gym-admin-password-notice__row">
          <code
            ref={textRef}
            className="gym-admin-password-notice__value"
            onClick={selectPasswordText}
          >
            {password}
          </code>
          <button type="button" className="gym-button gym-button--secondary" onClick={handleCopy}>
            {copyState === 'copied' ? 'Copiada' : 'Copiar'}
          </button>
        </div>
        {copyState === 'error' && (
          <p className="gym-admin-password-notice__error">
            No se pudo copiar automáticamente: seleccionamos el texto, copialo con Ctrl/Cmd+C.
          </p>
        )}
        <div className="gym-admin-password-notice__actions">
          <button type="button" className="gym-button gym-button--primary" onClick={onClose}>
            Cerrar aviso
          </button>
        </div>
      </div>
    </div>
  );
}
